import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import * as XLSX from 'xlsx'
import { apiLabs } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import Denied from '../../../Components/Denied'
import history from '../../../Services/history'
import { store } from '../../../store'
import {
  parseTabela, parseFechamento, calcular, cepFmt, norm,
} from './freteCore'
import {
  GlobalStyles, PageWrap, Topbar, Body,
  SlotRow, Slot, UpBtn,
  Hero, Tabs, Panel, Grid2,
  TableWrap, Bar, Chip,
  Filters, Btn, Strip, Pagination,
  Notice, AlertList, Alert, Chart, Empty, Toast,
} from './style'

// Cache local. Só o navegador de quem usa — nada vai pro banco, como pedido.
// A validade existe porque fechamento é quinzenal: passou disso, o que está
// guardado é de outro período e reabrir com ele na tela engana mais do que ajuda.
const LS_KEY = 'santolog-fechamento-frete-v1'
const LS_MAX_AGE_MS = 15 * 24 * 60 * 60 * 1000

const PAGE_SIZE = 100

// Mesma referência sempre, pros memos não reagirem a um array vazio novo.
const EMPTY_ROWS = []

// O que cada marca de atenção significa na hora de conferir a fatura.
const FLAGS = {
  cep: { t: 'CEP fora da tabela', d: 'Sem faixa de CEP correspondente, por isso sem valor de frete.', bad: true },
  pesoMax: { t: 'Peso acima do limite da tabela', d: 'Mais pesado que a última linha da tabela de preços, sem valor.', bad: true },
  semPreco: { t: 'Tarifa sem preço', d: 'A faixa de CEP aponta para uma tarifa sem coluna de preço.', bad: true },
  peso0: { t: 'Peso zerado', d: 'Peso 0 kg no fechamento. Foi cobrado o valor da primeira faixa.', bad: false },
  naoEntregue: { t: 'Não entregue', d: 'Devolução, recusa ou NF cancelada. O frete entra no total; confira se é cobrável.', bad: false },
  dup: { t: 'NF repetida no fechamento', d: 'A mesma nota aparece mais de uma vez.', bad: false },
}

const PESO_BUCKETS = [
  [0, 10, 'Até 10 kg'], [10, 20, '10 a 20 kg'], [20, 30, '20 a 30 kg'],
  [30, 50, '30 a 50 kg'], [50, 100, '50 a 100 kg'], [100, Infinity, 'Acima de 100 kg'],
]

// ── Formatação ─────────────────────────────────────────────────────────────
const brl = (v) => (v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
const n0 = (v) => Math.round(v || 0).toLocaleString('pt-BR')
const kg = (v) => (v == null ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 }))
const pct = (v) => `${((v || 0) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
const dBR = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '')
const dShort = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '')
const dtBR = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

const bucketOf = (p) => {
  const w = p.peso || 0
  for (const b of PESO_BUCKETS) if (w <= b[1] && (w > b[0] || b[0] === 0)) return b[2]
  return PESO_BUCKETS[PESO_BUCKETS.length - 1][2]
}
const ocLabel = (r) => (r.oc ? r.oc.charAt(0) + r.oc.slice(1).toLowerCase() : 'Sem ocorrência')

// ── Agregações ─────────────────────────────────────────────────────────────
function agg(rows, keyFn) {
  const m = new Map()
  for (const r of rows) {
    const k = keyFn(r)
    let a = m.get(k)
    if (!a) { a = { k, n: 0, peso: 0, frete: 0, comValor: 0, min: Infinity, max: -Infinity }; m.set(k, a) }
    a.n += 1
    a.peso += r.peso || 0
    if (r.frete != null) {
      a.frete += r.frete
      a.comValor += 1
      a.min = Math.min(a.min, r.frete)
      a.max = Math.max(a.max, r.frete)
    }
  }
  // A média divide pelos que TÊM valor, não pelo total: pedido sem valor
  // puxaria a média pra baixo fingindo um frete barato.
  for (const a of m.values()) {
    a.medio = a.comValor ? a.frete / a.comValor : null
    if (!a.comValor) { a.min = null; a.max = null }
  }
  return [...m.values()]
}

function totals(rows) {
  const t = { n: rows.length, peso: 0, frete: 0, comValor: 0, vol: 0 }
  for (const r of rows) {
    t.peso += r.peso || 0
    t.vol += r.vol || 0
    if (r.frete != null) { t.frete += r.frete; t.comValor += 1 }
  }
  t.medio = t.comValor ? t.frete / t.comValor : null
  t.porKg = t.peso ? t.frete / t.peso : null
  return t
}

function sortRows(rows, key, dir) {
  const m = dir === 'desc' ? -1 : 1
  return rows.slice().sort((a, b) => {
    let x = a[key]
    let y = b[key]
    // NF é texto na planilha mas ordena como número.
    if (key === 'nf') { x = Number(x) || 0; y = Number(y) || 0 }
    if (x == null) return 1
    if (y == null) return -1
    if (typeof x === 'string') return x.localeCompare(y, 'pt-BR') * m
    return (x - y) * m
  })
}

const searchHit = (r, q) => {
  if (!q) return true
  const s = norm(q)
  const cepd = q.replace(/\D/g, '')
  return (
    norm(r.nf).includes(s) || norm(r.dest).includes(s) ||
    norm(r.cidade).includes(s) || norm(r.bairro).includes(s) ||
    (cepd.length >= 3 && String(r.cep).padStart(8, '0').includes(cepd))
  )
}

const niceStep = (x) => {
  const p = Math.pow(10, Math.floor(Math.log10(x || 1)))
  const f = (x || 1) / p
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p
}
const shortBRL = (v) =>
  v >= 1000
    ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
    : `R$ ${Math.round(v)}`

// ── Tabela de agregação reaproveitada em 5 painéis ─────────────────────────
function AggTable({ rows, label, minmax, onPick }) {
  const tot = rows.reduce(
    (a, r) => ({ n: a.n + r.n, peso: a.peso + r.peso, frete: a.frete + r.frete, cv: a.cv + r.comValor }),
    { n: 0, peso: 0, frete: 0, cv: 0 }
  )
  const maxF = Math.max(...rows.map((r) => r.frete), 0)
  return (
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>{label}</th>
            <th className="n">Pedidos</th>
            <th className="n">%</th>
            <th className="n">Peso (kg)</th>
            <th className="n">Frete total</th>
            <th className="n">Frete médio</th>
            {minmax && <th className="n">Menor</th>}
            {minmax && <th className="n">Maior</th>}
            <th>Participação</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k}>
              <td>
                {onPick
                  ? <Btn type="button" onClick={() => onPick(r.k)}>{r.k}</Btn>
                  : r.k}
              </td>
              <td className="n mono">{n0(r.n)}</td>
              <td className="n mono">{pct(tot.n ? r.n / tot.n : 0)}</td>
              <td className="n mono">{kg(Math.round(r.peso * 10) / 10)}</td>
              <td className="n mono">{brl(r.frete)}</td>
              <td className="n mono">{brl(r.medio)}</td>
              {minmax && <td className="n mono">{brl(r.min)}</td>}
              {minmax && <td className="n mono">{brl(r.max)}</td>}
              <td><Bar $pct={maxF ? Math.max(2, (r.frete / maxF) * 100) : 0}><i /></Bar></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td className="n mono">{n0(tot.n)}</td>
            <td className="n mono">100%</td>
            <td className="n mono">{kg(Math.round(tot.peso * 10) / 10)}</td>
            <td className="n mono">{brl(tot.frete)}</td>
            <td className="n mono">{brl(tot.cv ? tot.frete / tot.cv : null)}</td>
            {minmax && <td />}
            {minmax && <td />}
            <td />
          </tr>
        </tfoot>
      </table>
    </TableWrap>
  )
}

// ── Gráfico de frete por dia ───────────────────────────────────────────────
function DailyChart({ linhas }) {
  const boxRef = useRef(null)
  const tipRef = useRef(null)
  const [width, setWidth] = useState(720)

  useEffect(() => {
    const measure = () => {
      if (boxRef.current) setWidth(Math.max(300, boxRef.current.clientWidth - 24))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const dados = useMemo(() => {
    const m = new Map()
    linhas.forEach((r) => {
      const d = (r.dtFin || r.dtInc || '').slice(0, 10)
      if (!d) return
      const a = m.get(d) || { d, frete: 0, n: 0 }
      a.frete += r.frete || 0
      a.n += 1
      m.set(d, a)
    })
    return [...m.values()].sort((a, b) => (a.d < b.d ? -1 : 1))
  }, [linhas])

  if (!dados.length) return <Notice>Nenhuma entrega com data para montar o gráfico.</Notice>

  const H = width < 520 ? 200 : 240
  const pl = 62; const pb = 26; const pt = 8; const pr = 8
  const max = Math.max(...dados.map((x) => x.frete), 1)
  const step = niceStep(max / 4)
  const top = Math.ceil(max / step) * step
  const bw = (width - pl - pr) / Math.max(dados.length, 1)
  const y = (v) => pt + (H - pt - pb) * (1 - v / top)
  // Um rótulo a cada N barras, pra não empilhar texto em fechamento longo.
  const every = Math.max(1, Math.ceil(dados.length / Math.floor((width - pl) / 46)))

  const linhasGrade = []
  for (let v = 0; v <= top + 1e-6; v += step) linhasGrade.push(v)

  const show = (e, x) => {
    const tip = tipRef.current
    const box = boxRef.current
    if (!tip || !box) return
    tip.textContent = `${dBR(x.d)} · ${brl(x.frete)} · ${n0(x.n)} pedidos`
    tip.style.opacity = 1
    const bb = box.getBoundingClientRect()
    let lx = e.clientX - bb.left + 12
    if (lx + tip.offsetWidth > bb.width - 4) lx = e.clientX - bb.left - tip.offsetWidth - 12
    tip.style.left = `${Math.max(4, lx)}px`
    tip.style.top = `${e.clientY - bb.top - 34}px`
  }
  const hide = () => { if (tipRef.current) tipRef.current.style.opacity = 0 }

  return (
    <Chart ref={boxRef}>
      <div className="tip" ref={tipRef} />
      <svg viewBox={`0 0 ${width} ${H}`} width="100%" role="img" aria-label="Frete por dia de entrega">
        {linhasGrade.map((v) => (
          <g key={v}>
            <line x1={pl} x2={width - pr} y1={y(v)} y2={y(v)} stroke="#1f2430" />
            <text x={pl - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill="#5a6370">{shortBRL(v)}</text>
          </g>
        ))}
        {dados.map((x, i) => {
          const bx = pl + i * bw + bw * 0.15
          const w = bw * 0.7
          return (
            <g key={x.d}>
              <rect
                x={bx} y={y(x.frete)} width={w} height={Math.max(0, y(0) - y(x.frete))}
                rx="2" fill="#00b87a"
                onMouseMove={(e) => show(e, x)} onMouseLeave={hide}
              />
              {i % every === 0 && (
                <text x={bx + w / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#5a6370">
                  {dShort(x.d)}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    </Chart>
  )
}

// ── Cabeçalho ordenável ────────────────────────────────────────────────────
function Th({ label, sortKey, state, setState, cls = '' }) {
  const active = state.sort === sortKey
  return (
    <th
      className={`sort ${cls}`}
      data-dir={active ? state.dir : undefined}
      onClick={() =>
        setState((s) => ({
          ...s,
          sort: sortKey,
          // Número começa no maior; texto, em ordem alfabética.
          dir: s.sort === sortKey
            ? (s.dir === 'asc' ? 'desc' : 'asc')
            : (['frete', 'peso', 'vol'].includes(sortKey) ? 'desc' : 'asc'),
          page: 0,
        }))
      }
    >
      {label}{active ? (state.dir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  )
}

function Pager({ state, setState, total }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const page = Math.min(state.page, pages - 1)
  return (
    <Pagination>
      <span className="page">
        {n0(Math.min(total, page * PAGE_SIZE + 1))}–{n0(Math.min(total, (page + 1) * PAGE_SIZE))} de {n0(total)}
      </span>
      <Btn type="button" disabled={page <= 0} onClick={() => setState((s) => ({ ...s, page: s.page - 1 }))}>Anterior</Btn>
      <Btn type="button" disabled={page >= pages - 1} onClick={() => setState((s) => ({ ...s, page: s.page + 1 }))}>Próxima</Btn>
    </Pagination>
  )
}

const flagChips = (r) =>
  r.flags.map((f) => <Chip key={f} $bad={FLAGS[f].bad}>{FLAGS[f].t}</Chip>)

// ═══════════════════════════════════════════════════════════════════════════
export default function SantoLogFechamentoFrete(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line

  const [tabela, setTabela] = useState(null)
  const [fech, setFech] = useState(null)
  const [meta, setMeta] = useState({ tabela: null, fechamento: null })
  const [tab, setTab] = useState('resumo')
  const [busy, setBusy] = useState('')
  const [toast, setToast] = useState('')
  const [tabelaAll, setTabelaAll] = useState(false)

  const [ped, setPed] = useState({ q: '', tarifa: '', rota: '', oc: '', flag: '', sort: 'idx', dir: 'asc', page: 0 })
  const [nfState, setNfState] = useState({ q: '', tarifa: '', sort: 'idx', dir: 'asc', page: 0 })

  const fileTabela = useRef(null)
  const fileFech = useRef(null)
  const toastTimer = useRef(null)

  const say = useCallback((msg) => {
    setToast(msg)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(''), 4000)
  }, [])

  // ── Cache do navegador ───────────────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY)
      if (!raw) return
      const cur = JSON.parse(raw)
      const fresh = (slot) =>
        slot && slot.meta && Date.now() - new Date(slot.meta.savedAt).getTime() < LS_MAX_AGE_MS
      const next = { tabela: null, fechamento: null }
      if (fresh(cur.tabela)) { setTabela(cur.tabela.data); next.tabela = cur.tabela.meta }
      if (fresh(cur.fechamento)) { setFech(cur.fechamento.data); next.fechamento = cur.fechamento.meta }
      setMeta(next)
    } catch (e) {
      // Cache corrompido ou storage bloqueado: a tela abre vazia, que é o
      // estado correto. Não vale derrubar a página por causa disso.
      console.warn('[FechamentoFrete] cache local indisponível:', e)
    }
  }, [])

  const cache = useCallback((kind, data, metaInfo) => {
    try {
      const cur = JSON.parse(localStorage.getItem(LS_KEY) || '{}')
      cur[kind] = { meta: metaInfo, data }
      localStorage.setItem(LS_KEY, JSON.stringify(cur))
      return true
    } catch (e) {
      // Planilha grande estoura a cota do localStorage. A tela continua
      // funcionando nesta sessão — só não sobrevive ao F5.
      console.warn('[FechamentoFrete] não deu pra guardar no cache:', e)
      return false
    }
  }, [])

  async function handleFile(e, kind) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    setBusy(kind)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const parsed = kind === 'tabela'
        ? parseTabela(XLSX, wb, file.name)
        : parseFechamento(XLSX, wb, file.name)
      const metaInfo = {
        fileName: file.name,
        savedAt: new Date().toISOString(),
        rows: kind === 'tabela' ? parsed.faixas.length : parsed.pedidos.length,
      }
      if (kind === 'tabela') setTabela(parsed); else setFech(parsed)
      setMeta((m) => ({ ...m, [kind]: metaInfo }))
      setPed((s) => ({ ...s, page: 0 }))
      setNfState((s) => ({ ...s, page: 0 }))
      const ok = cache(kind, parsed, metaInfo)
      say(`${kind === 'tabela' ? 'Tabela' : 'Fechamento'} carregado. ${ok ? 'Fica no navegador até o próximo envio.' : 'Vale só nesta sessão.'}`)
    } catch (err) {
      console.error(err)
      say(`Não deu para ler "${file.name}": ${err.message || err}`)
    } finally {
      setBusy('')
    }
  }

  function limpar() {
    try { localStorage.removeItem(LS_KEY) } catch (err) { /* storage bloqueado */ }
    setTabela(null); setFech(null)
    setMeta({ tabela: null, fechamento: null })
    say('Planilhas removidas desta tela e do navegador.')
  }

  // ── Cálculo ──────────────────────────────────────────────────────────────
  const calc = useMemo(() => (tabela && fech ? calcular(tabela, fech) : null), [tabela, fech])
  // Precisa de referência estável: sem o useMemo, o `[]` do caso vazio seria
  // um array novo a cada render e invalidaria todos os memos abaixo — o
  // cruzamento de ~2 mil pedidos refeito a cada tecla digitada no filtro.
  const L = useMemo(() => (calc ? calc.linhas : EMPTY_ROWS), [calc])
  const T = useMemo(() => totals(L), [L])

  const pedFiltered = useMemo(
    () => L.filter((r) =>
      searchHit(r, ped.q) &&
      (!ped.tarifa || (ped.tarifa === '__vazio' ? !r.tarifa : r.tarifa === ped.tarifa)) &&
      (!ped.rota || (ped.rota === '__vazio' ? !r.rota : r.rota === ped.rota)) &&
      (!ped.oc || r.oc === ped.oc) &&
      (!ped.flag || r.flags.includes(ped.flag))
    ),
    [L, ped.q, ped.tarifa, ped.rota, ped.oc, ped.flag]
  )
  const nfFiltered = useMemo(
    () => L.filter((r) =>
      searchHit(r, nfState.q) &&
      (!nfState.tarifa || (nfState.tarifa === '__vazio' ? !r.tarifa : r.tarifa === nfState.tarifa))
    ),
    [L, nfState.q, nfState.tarifa]
  )

  function exportXlsx(base, data) {
    if (!data.length) { say('Nada para exportar com esses filtros.'); return }
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Dados')
    XLSX.writeFile(wb, `${base}-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const slot = (kind, titulo, vazio, ref) => {
    const m = meta[kind]
    return (
      <Slot $ok={!!m}>
        <div className="t">
          <b>{titulo}</b>
          <small title={m ? m.fileName : vazio}>
            {m ? <>{m.fileName} <span className="when">· {dtBR(m.savedAt)} · {n0(m.rows)} linhas</span></> : vazio}
          </small>
        </div>
        <UpBtn type="button" disabled={busy === kind} onClick={() => ref.current.click()}>
          {busy === kind ? 'Lendo…' : m ? 'Trocar' : 'Enviar'}
        </UpBtn>
      </Slot>
    )
  }

  const tarifaOptions = useMemo(() => {
    const t = [...new Set(L.map((r) => r.tarifa).filter(Boolean))].sort().map((x) => [x, x])
    if (L.some((r) => !r.tarifa)) t.push(['__vazio', 'Sem tarifa'])
    return t
  }, [L])

  return (
    <Dashboard title="Fechamento Frete">
      <GlobalStyles />
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <PageWrap>
          <Topbar>
            <h2>Fechamento de frete</h2>
            <span className="crumb">SantoLog · conferência da fatura da transportadora</span>
            <span className="spacer" />
            {(tabela || fech) && (
              <Btn type="button" onClick={limpar}>Limpar planilhas</Btn>
            )}
          </Topbar>

          <SlotRow>
            {slot('tabela', 'Tabela de frete', 'Nenhuma tabela carregada', fileTabela)}
            {slot('fechamento', 'Fechamento', 'Nenhum fechamento carregado', fileFech)}
          </SlotRow>
          <input ref={fileTabela} type="file" accept=".xlsx,.xls,.xlsm" hidden
            onChange={(e) => handleFile(e, 'tabela')} />
          <input ref={fileFech} type="file" accept=".xlsx,.xls,.xlsm,.csv" hidden
            onChange={(e) => handleFile(e, 'fechamento')} />

          <Body>
            {!calc ? (
              <Empty>
                <h3>
                  Envie {!tabela && !fech ? 'a tabela de frete e o fechamento'
                    : !tabela ? 'a tabela de frete' : 'o fechamento'}
                </h3>
                <p>
                  A tabela traz as faixas de CEP e o preço por peso; o fechamento traz as entregas
                  da quinzena. O cruzamento sai assim que os dois estiverem carregados. Nada é
                  enviado para o servidor — as planilhas ficam só neste navegador.
                </p>
              </Empty>
            ) : (
              <>
                <Resumo L={L} T={T} tabela={tabela} fech={fech}
                  tab={tab} setTab={setTab}
                  onFlag={(f) => { setPed((s) => ({ ...s, q: '', tarifa: '', rota: '', oc: '', flag: f, page: 0 })); setTab('pedidos') }}
                  onRota={(r) => { setPed((s) => ({ ...s, q: '', tarifa: '', oc: '', flag: '', rota: r === 'Sem rota' ? '__vazio' : r, page: 0 })); setTab('pedidos') }}
                  ped={ped} setPed={setPed} pedFiltered={pedFiltered}
                  nfState={nfState} setNfState={setNfState} nfFiltered={nfFiltered}
                  tarifaOptions={tarifaOptions}
                  tabelaAll={tabelaAll} setTabelaAll={setTabelaAll}
                  exportXlsx={exportXlsx}
                />
              </>
            )}
          </Body>

          <Toast $on={!!toast} role="status" aria-live="polite">{toast}</Toast>
        </PageWrap>
      )}
    </Dashboard>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Corpo com as quatro abas. Separado só pra função principal caber na tela.
function Resumo({
  L, T, tabela, fech, tab, setTab, onFlag, onRota,
  ped, setPed, pedFiltered, nfState, setNfState, nfFiltered,
  tarifaOptions, tabelaAll, setTabelaAll, exportXlsx,
}) {
  const datas = L.map((r) => r.dtFin || r.dtInc).filter(Boolean).sort()
  const periodo = datas.length ? `Entregas de ${dBR(datas[0])} a ${dBR(datas[datas.length - 1])}` : ''
  const semValor = L.filter((r) => r.frete == null).length

  const TABS = [['resumo', 'Resumo'], ['pedidos', 'Pedidos'], ['nf', 'NF × tabela'], ['tabela', 'Tabela de frete']]

  return (
    <>
      <Hero>
        <div className="total">
          <div className="lab">Frete total do fechamento</div>
          <div className="val">{brl(T.frete)}</div>
          <div className="per">{periodo}</div>
        </div>
        <div className="stats">
          <div className="stat"><div className="lab">Pedidos</div><div className="val">{n0(T.n)}</div></div>
          <div className="stat"><div className="lab">Frete médio</div><div className="val">{brl(T.medio)}</div></div>
          <div className="stat"><div className="lab">Peso total</div><div className="val">{kg(Math.round(T.peso))} kg</div></div>
          <div className="stat"><div className="lab">Frete por kg</div><div className="val">{brl(T.porKg)}</div></div>
          <div className="stat">
            <div className="lab">Sem valor</div>
            <div className={`val ${semValor ? 'alert' : ''}`}>{n0(semValor)}</div>
          </div>
        </div>
      </Hero>

      <Tabs role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </Tabs>

      {tab === 'resumo' && <AbaResumo L={L} T={T} tabela={tabela} fech={fech} onFlag={onFlag} onRota={onRota} />}
      {tab === 'pedidos' && (
        <AbaPedidos L={L} ped={ped} setPed={setPed} rows={pedFiltered}
          tarifaOptions={tarifaOptions} exportXlsx={exportXlsx} />
      )}
      {tab === 'nf' && (
        <AbaNF nfState={nfState} setNfState={setNfState} rows={nfFiltered}
          tarifaOptions={tarifaOptions} exportXlsx={exportXlsx} />
      )}
      {tab === 'tabela' && <AbaTabela tabela={tabela} all={tabelaAll} setAll={setTabelaAll} />}
    </>
  )
}

function AbaResumo({ L, T, tabela, fech, onFlag, onRota }) {
  const porTarifa = agg(L, (r) => r.tarifa || 'Sem tarifa (CEP fora da tabela)').sort((a, b) => b.frete - a.frete)
  const porPeso = agg(L, bucketOf).sort(
    (a, b) => PESO_BUCKETS.findIndex((x) => x[2] === a.k) - PESO_BUCKETS.findIndex((x) => x[2] === b.k)
  )
  const porRota = agg(L, (r) => r.rota || 'Sem rota').sort((a, b) => b.frete - a.frete)
  const porOc = agg(L, ocLabel).sort((a, b) => b.n - a.n)
  const porMot = agg(L, (r) => r.mot || 'Sem motorista').sort((a, b) => b.frete - a.frete)

  const fc = {}
  L.forEach((r) => r.flags.forEach((f) => { fc[f] = (fc[f] || 0) + 1 }))
  const fl = Object.keys(FLAGS).filter((k) => fc[k])
  const avisos = [...(tabela.avisos || []), ...(fech.avisos || [])]
  const minimo = L.filter((r) => r.frete != null && r.faixaPeso && r.faixaPeso.startsWith('até')).length

  return (
    <>
      <Panel>
        <h3>Por tipo de tarifa</h3>
        <p className="sub">
          {n0(minimo)} pedidos ({pct(T.n ? minimo / T.n : 0)}) caíram na primeira faixa de peso, a do frete mínimo.
        </p>
        <AggTable rows={porTarifa} label="Tipo de tarifa" minmax />
      </Panel>

      <Panel>
        <h3>Frete por dia de entrega</h3>
        <p className="sub">Soma do frete pela data de finalização da entrega. Passe o cursor nas barras para ver o valor.</p>
        <DailyChart linhas={L} />
      </Panel>

      <Grid2>
        <Panel>
          <h3>Por faixa de peso</h3>
          <p className="sub">Onde o frete se concentra conforme o peso do pedido.</p>
          <AggTable rows={porPeso} label="Faixa de peso" />
        </Panel>
        <Panel>
          <h3>Pontos de atenção</h3>
          <p className="sub">Pedidos que pedem conferência antes de fechar a fatura com a transportadora.</p>
          {avisos.map((a) => <Notice key={a}>{a}</Notice>)}
          <AlertList>
            {fl.length ? fl.map((k) => (
              <Alert key={k} $bad={FLAGS[k].bad}>
                <div className="c">{n0(fc[k])}</div>
                <div className="d"><b>{FLAGS[k].t}</b><small>{FLAGS[k].d}</small></div>
                <Btn type="button" onClick={() => onFlag(k)}>Ver pedidos</Btn>
              </Alert>
            )) : <p className="sub">Nenhum pedido com pendência.</p>}
          </AlertList>
        </Panel>
      </Grid2>

      <Panel>
        <h3>Por rota</h3>
        <p className="sub">Ordenado pelo frete total. Clique numa rota para ver os pedidos dela.</p>
        <AggTable rows={porRota} label="Rota" onPick={onRota} />
      </Panel>

      <Grid2>
        <Panel>
          <h3>Por situação da entrega</h3>
          <p className="sub">Ocorrência registrada pelo motorista na finalização.</p>
          <AggTable rows={porOc} label="Ocorrência" />
        </Panel>
        <Panel>
          <h3>Por motorista</h3>
          <p className="sub">Frete gerado pelas entregas de cada motorista.</p>
          <AggTable rows={porMot} label="Motorista" />
        </Panel>
      </Grid2>
    </>
  )
}

function AbaPedidos({ L, ped, setPed, rows, tarifaOptions, exportXlsx }) {
  const sorted = sortRows(rows, ped.sort, ped.dir)
  const T = totals(sorted)
  const page = sorted.slice(ped.page * PAGE_SIZE, (ped.page + 1) * PAGE_SIZE)

  const rotas = [...new Set(L.map((r) => r.rota).filter(Boolean))].sort().map((x) => [x, x])
  if (L.some((r) => !r.rota)) rotas.push(['__vazio', 'Sem rota'])
  const ocs = [...new Set(L.map((r) => r.oc).filter(Boolean))].sort()
    .map((o) => [o, o.charAt(0) + o.slice(1).toLowerCase()])
  const fls = Object.keys(FLAGS).filter((k) => L.some((r) => r.flags.includes(k))).map((k) => [k, FLAGS[k].t])

  const set = (patch) => setPed((s) => ({ ...s, ...patch, page: 0 }))

  return (
    <Panel>
      <Filters>
        <input type="search" placeholder="Buscar NF, destinatário, cidade, bairro ou CEP"
          value={ped.q} onChange={(e) => set({ q: e.target.value })} aria-label="Buscar" />
        <select value={ped.tarifa} onChange={(e) => set({ tarifa: e.target.value })} aria-label="Tipo de tarifa">
          <option value="">Todas as tarifas</option>
          {tarifaOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={ped.rota} onChange={(e) => set({ rota: e.target.value })} aria-label="Rota">
          <option value="">Todas as rotas</option>
          {rotas.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={ped.oc} onChange={(e) => set({ oc: e.target.value })} aria-label="Ocorrência">
          <option value="">Todas as ocorrências</option>
          {ocs.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={ped.flag} onChange={(e) => set({ flag: e.target.value })} aria-label="Pendência">
          <option value="">Com ou sem pendência</option>
          {fls.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Btn type="button" onClick={() => set({ q: '', tarifa: '', rota: '', oc: '', flag: '' })}>Limpar filtros</Btn>
        <Btn type="button" $primary onClick={() => exportXlsx('pedidos-frete', sorted.map((r) => ({
          'NF': r.nf, 'Data entrega': dBR(r.dtFin), 'Destinatário': r.dest,
          'Cidade': r.cidade, 'Bairro': r.bairro, 'UF': r.uf, 'CEP': cepFmt(r.cep),
          'Rota': r.rota, 'Motorista': r.mot, 'Volumes': r.vol, 'Peso (kg)': r.peso,
          'Tipo tarifa': r.tarifa, 'Faixa de peso': r.faixaPeso, 'Frete (R$)': r.frete,
          'Ocorrência': r.oc, 'Status': r.status,
          'Atenção': r.flags.map((f) => FLAGS[f].t).join('; '),
        })))}>Exportar Excel</Btn>
      </Filters>

      <Strip>
        <span>Pedidos<b>{n0(T.n)}</b></span>
        <span>Frete<b>{brl(T.frete)}</b></span>
        <span>Médio<b>{brl(T.medio)}</b></span>
        <span>Peso<b>{kg(Math.round(T.peso * 10) / 10)} kg</b></span>
        <span>Volumes<b>{n0(T.vol)}</b></span>
      </Strip>

      <TableWrap>
        <table>
          <thead>
            <tr>
              <Th label="NF" sortKey="nf" state={ped} setState={setPed} />
              <Th label="Entrega" sortKey="dtFin" state={ped} setState={setPed} />
              <Th label="Destinatário" sortKey="dest" state={ped} setState={setPed} />
              <Th label="Cidade" sortKey="cidade" state={ped} setState={setPed} />
              <Th label="Bairro" sortKey="bairro" state={ped} setState={setPed} />
              <Th label="CEP" sortKey="cep" state={ped} setState={setPed} />
              <Th label="Rota" sortKey="rota" state={ped} setState={setPed} />
              <Th label="Motorista" sortKey="mot" state={ped} setState={setPed} />
              <Th label="Vol." sortKey="vol" state={ped} setState={setPed} cls="n" />
              <Th label="Peso (kg)" sortKey="peso" state={ped} setState={setPed} cls="n" />
              <Th label="Tarifa" sortKey="tarifa" state={ped} setState={setPed} />
              <Th label="Frete" sortKey="frete" state={ped} setState={setPed} cls="n" />
              <Th label="Ocorrência" sortKey="oc" state={ped} setState={setPed} />
              <th>Atenção</th>
            </tr>
          </thead>
          <tbody>
            {page.length === 0 ? (
              <tr><td colSpan={14} className="dim">Nenhum pedido com esses filtros.</td></tr>
            ) : page.map((r) => (
              <tr key={`${r.idx}-${r.nf}`}>
                <td className="mono">{r.nf}</td>
                <td className="mono">{dBR(r.dtFin)}</td>
                <td>{r.dest}</td>
                <td>{r.cidade}</td>
                <td>{r.bairro}</td>
                <td className="mono">{cepFmt(r.cep)}</td>
                <td>{r.rota}</td>
                <td>{r.mot}</td>
                <td className="n mono">{n0(r.vol)}</td>
                <td className="n mono">{kg(r.peso)}</td>
                <td>{r.tarifa || <span className="vnull">—</span>}</td>
                <td className="n mono">{r.frete == null ? <span className="vnull">sem valor</span> : brl(r.frete)}</td>
                <td>{ocLabel(r)}</td>
                <td>{flagChips(r)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={8}>Total filtrado</td>
              <td className="n mono">{n0(T.vol)}</td>
              <td className="n mono">{kg(Math.round(T.peso * 10) / 10)}</td>
              <td />
              <td className="n mono">{brl(T.frete)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </TableWrap>
      <Pager state={ped} setState={setPed} total={sorted.length} />
    </Panel>
  )
}

function AbaNF({ nfState, setNfState, rows, tarifaOptions, exportXlsx }) {
  const sorted = sortRows(rows, nfState.sort, nfState.dir)
  const T = totals(sorted)
  const page = sorted.slice(nfState.page * PAGE_SIZE, (nfState.page + 1) * PAGE_SIZE)

  return (
    <Panel>
      <h3>Cruzamento das notas com a tabela de frete</h3>
      <p className="sub">
        Cada nota do fechamento com o peso e o CEP enviados, a faixa de CEP e a faixa de peso
        encontradas na tabela, e o valor resultante.
      </p>
      <Filters>
        <input type="search" placeholder="Buscar NF ou CEP" value={nfState.q}
          onChange={(e) => setNfState((s) => ({ ...s, q: e.target.value, page: 0 }))} aria-label="Buscar NF ou CEP" />
        <select value={nfState.tarifa} onChange={(e) => setNfState((s) => ({ ...s, tarifa: e.target.value, page: 0 }))} aria-label="Tipo de tarifa">
          <option value="">Todas as tarifas</option>
          {tarifaOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Btn type="button" $primary onClick={() => exportXlsx('nf-x-tabela', sorted.map((r) => ({
          'Nota fiscal': r.nf, 'Peso (kg)': r.peso, 'CEP': cepFmt(r.cep),
          'Faixa de CEP': r.faixaCep, 'Tipo tarifa': r.tarifa,
          'Faixa de peso': r.faixaPeso, 'Valor tabela (R$)': r.frete,
        })))}>Exportar Excel</Btn>
      </Filters>

      <Strip>
        <span>Notas<b>{n0(T.n)}</b></span>
        <span>Valor total<b>{brl(T.frete)}</b></span>
        <span>Peso total<b>{kg(Math.round(T.peso * 10) / 10)} kg</b></span>
      </Strip>

      <TableWrap>
        <table>
          <thead>
            <tr>
              <Th label="Nota fiscal" sortKey="nf" state={nfState} setState={setNfState} />
              <Th label="Peso (kg)" sortKey="peso" state={nfState} setState={setNfState} cls="n" />
              <Th label="CEP" sortKey="cep" state={nfState} setState={setNfState} />
              <Th label="Faixa de CEP" sortKey="faixaCep" state={nfState} setState={setNfState} />
              <Th label="Tipo tarifa" sortKey="tarifa" state={nfState} setState={setNfState} />
              <Th label="Faixa de peso" sortKey="faixaPeso" state={nfState} setState={setNfState} />
              <Th label="Valor tabela" sortKey="frete" state={nfState} setState={setNfState} cls="n" />
            </tr>
          </thead>
          <tbody>
            {page.length === 0 ? (
              <tr><td colSpan={7} className="dim">Nenhuma nota encontrada.</td></tr>
            ) : page.map((r) => (
              <tr key={`${r.idx}-${r.nf}`}>
                <td className="mono">{r.nf}</td>
                <td className="n mono">{kg(r.peso)}</td>
                <td className="mono">{cepFmt(r.cep)}</td>
                <td className="mono">{r.faixaCep || <span className="vnull">não encontrada</span>}</td>
                <td>{r.tarifa || '—'}</td>
                <td>{r.faixaPeso || (r.flags.includes('pesoMax') ? <span className="vnull">acima da tabela</span> : '—')}</td>
                <td className="n mono">{r.frete == null ? <span className="vnull">sem valor</span> : brl(r.frete)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="n mono">{kg(Math.round(T.peso * 10) / 10)}</td>
              <td colSpan={4} />
              <td className="n mono">{brl(T.frete)}</td>
            </tr>
          </tfoot>
        </table>
      </TableWrap>
      <Pager state={nfState} setState={setNfState} total={sorted.length} />
    </Panel>
  )
}

function AbaTabela({ tabela, all, setAll }) {
  if (!tabela) return null
  // A aba de preços passa de 100 linhas em algumas tabelas: mostra as
  // primeiras e deixa abrir o resto, pra não travar a rolagem.
  const pesos = all ? tabela.pesos : tabela.pesos.slice(0, 40)
  return (
    <Grid2>
      <Panel>
        <h3>Faixas de CEP</h3>
        <p className="sub">
          {n0(tabela.faixas.length)} faixas lidas de "{tabela.fileName}". CEPs fora destas faixas ficam sem valor.
        </p>
        {(tabela.avisos || []).map((a) => <Notice key={a}>{a}</Notice>)}
        <TableWrap style={{ maxHeight: 460 }}>
          <table>
            <thead>
              <tr>
                <th>CEP inicial</th><th>CEP final</th><th className="n">Prazo (dias)</th>
                <th>Tipo tarifa</th><th>UF</th><th className="n">Advalorem</th><th className="n">GRIS</th>
              </tr>
            </thead>
            <tbody>
              {tabela.faixas.map((f, i) => (
                <tr key={`${f.ini}-${f.fim}-${i}`}>
                  <td className="mono">{cepFmt(f.ini)}</td>
                  <td className="mono">{cepFmt(f.fim)}</td>
                  <td className="n mono">{f.prazo == null ? '—' : f.prazo}</td>
                  <td>{f.tarifa}</td>
                  <td>{f.uf}</td>
                  <td className="n mono">{kg(f.adv)}%</td>
                  <td className="n mono">{kg(f.gris)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Panel>

      <Panel>
        <h3>Preço por peso</h3>
        <p className="sub">
          {n0(tabela.pesos.length)} faixas, até {kg(tabela.pesos[tabela.pesos.length - 1].max)} kg.
        </p>
        <TableWrap style={{ maxHeight: 460 }}>
          <table>
            <thead>
              <tr>
                <th className="n">Peso mín. (kg)</th><th className="n">Peso máx. (kg)</th>
                {tabela.tarifas.map((x) => <th key={x} className="n">{x}</th>)}
              </tr>
            </thead>
            <tbody>
              {pesos.map((p, i) => (
                <tr key={`${p.min}-${p.max}-${i}`}>
                  <td className="n mono">{kg(p.min)}</td>
                  <td className="n mono">{kg(p.max)}</td>
                  {tabela.tarifas.map((x) => (
                    <td key={x} className="n mono">{brl(p.valores[x] == null ? null : p.valores[x])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        {tabela.pesos.length > 40 && (
          <Pagination>
            <Btn type="button" onClick={() => setAll(!all)}>
              {all ? 'Mostrar só as primeiras 40' : `Mostrar todas as ${n0(tabela.pesos.length)} faixas`}
            </Btn>
          </Pagination>
        )}
      </Panel>
    </Grid2>
  )
}
